import { useState, useRef, useEffect } from "react";
import { requestSigningLink, checkSigningStatus } from "../lib/signnow";

const POLL_INTERVAL_MS = 5000;

/**
 * useSignNow
 *
 * Encapsulates the full SignNow signing lifecycle:
 *   idle → loading → ready (iframe visible + polling) → done | error
 *
 * Usage:
 *   const { state, signingUrl, errorMsg, initSigning, confirmSigned } = useSignNow({
 *     templateId, signerEmail, signerName, documentName,
 *     onSigned: (docKey) => { ... },
 *     docKey,
 *   });
 *
 * States:
 *   "idle"    — not started
 *   "loading" — waiting for /api/signnow/invite response
 *   "ready"   — signingUrl available, iframe should be shown, polling active
 *   "error"   — initSigning failed; errorMsg contains reason
 *
 * confirmSigned() — manual fallback for users who signed in a new tab;
 *                   stops polling and fires onSigned immediately.
 */
export function useSignNow({ templateId, signerEmail, signerName, documentName, docKey, onSigned }) {
  const [state, setState]         = useState("idle");
  const [signingUrl, setSigningUrl] = useState(null);
  const [errorMsg, setErrorMsg]   = useState(null);
  const pollRef                   = useRef(null);
  const docIdRef                  = useRef(null);

  // Clean up polling on unmount
  useEffect(() => () => clearInterval(pollRef.current), []);

  async function initSigning() {
    setState("loading");
    setErrorMsg(null);
    try {
      const { signingLink, documentId } = await requestSigningLink({
        templateId,
        signerEmail,
        signerName,
        documentName: documentName ?? `${docKey} — ${signerName}`,
      });
      setSigningUrl(signingLink);
      docIdRef.current = documentId;
      setState("ready");
      startPolling(documentId);
    } catch (err) {
      setErrorMsg(err.message);
      setState("error");
    }
  }

  function startPolling(documentId) {
    clearInterval(pollRef.current); // guard against double-start
    pollRef.current = setInterval(async () => {
      try {
        const { signed } = await checkSigningStatus(documentId);
        if (signed) confirmSigned();
      } catch {
        // transient network error — keep polling
      }
    }, POLL_INTERVAL_MS);
  }

  function confirmSigned() {
    clearInterval(pollRef.current);
    onSigned?.(docKey);
  }

  function retry() {
    setState("idle");
    setErrorMsg(null);
    setSigningUrl(null);
  }

  return { state, signingUrl, errorMsg, initSigning, confirmSigned, retry };
}